/**
 * IP-based Country Detection
 * Uses FREE API: ip-api.com (unlimited for non-commercial)
 * Fallback to browser language if API fails
 */

// Country code to language mapping (Only Vietnamese and English)
const COUNTRY_TO_LANGUAGE = {
  // Vietnam
  'VN': 'vi',
  
  // All other countries default to English
  // English-speaking countries
  'US': 'en',
  'GB': 'en',
  'CA': 'en',
  'AU': 'en',
  'NZ': 'en',
  'IE': 'en',
  'SG': 'en',
  'MY': 'en',
  'PH': 'en',
  'IN': 'en',
  'KH': 'en',
  'CN': 'en',
  'TW': 'en',
  'HK': 'en',
  'MO': 'en',
  'KR': 'en',
  'KP': 'en',
  'JP': 'en',
  'TH': 'en',
  'LA': 'en'
}

/**
 * Detect user's country by IP
 * @returns {Promise<string>} Country code (VN, US, KH, CN, KR, JP, TH)
 */
export async function detectCountryByIP() {
  try {
    // Use ipapi.co (FREE HTTPS API - 1000 requests/day)
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    })
    
    if (!response.ok) {
      throw new Error('IP API failed')
    }
    
    const data = await response.json()
    
    // ipapi.co returns country_code directly
    if (data.country_code) {
      return data.country_code
    }
    
    throw new Error('No country code in response')
  } catch (error) {
    console.warn('⚠️ IP detection failed:', error.message)
    
    // Fallback: Try alternative API (geojs.io)
    try {
      const geoResponse = await fetch('https://get.geojs.io/v1/ip/country.json')
      const geoData = await geoResponse.json()
      
      if (geoData.country) {
        return geoData.country
      }
    } catch (fallbackError) {
      console.warn('⚠️ Fallback IP detection also failed')
    }
    
    return null
  }
}

/**
 * Get language from country code
 * @param {string} countryCode - ISO country code (VN, US, etc.)
 * @returns {string} Language code (vi, en)
 */
export function getLanguageFromCountry(countryCode) {
  const language = COUNTRY_TO_LANGUAGE[countryCode]
  
  if (language) {
    return language
  }

  // Fallback to English for unknown countries
  return 'en'
}

/**
 * Detect browser language
 * @returns {string} Language code (vi, en)
 */
export function getBrowserLanguage() {
  const browserLang = navigator.language || navigator.userLanguage || 'en'
  const langCode = browserLang.split('-')[0].toLowerCase()
  
  // Only support Vietnamese and English
  const supportedLanguages = ['vi', 'en']
  
  if (supportedLanguages.includes(langCode)) {
    return langCode
  }

  // Default to English
  return 'en'
}

/**
 * Detect and set initial language
 * Priority: localStorage > IP detection > Browser language > English
 * @returns {Promise<string>} Language code
 */
export async function detectInitialLanguage() {
  // 1. Check if user has manually selected a language before
  const savedLanguage = localStorage.getItem('i18nextLng')
  if (savedLanguage && ['vi', 'en'].includes(savedLanguage)) {
    return savedLanguage
  }
  
  // 2. Try IP-based detection
  try {
    const countryCode = await detectCountryByIP()
    if (countryCode) {
      const language = getLanguageFromCountry(countryCode)
      return language
    }
  } catch (error) {
    console.warn('IP detection error:', error)
  }
  
  // 3. Fallback to browser language
  const browserLang = getBrowserLanguage()
  return browserLang
}

/**
 * Get flag emoji for language
 * @param {string} langCode - Language code
 * @returns {string} Flag emoji
 */
export function getLanguageFlag(langCode) {
  const flags = {
    'vi': '🇻🇳',
    'en': '🇬🇧'
  }
  
  return flags[langCode] || '🌐'
}

/**
 * Get language native name
 * @param {string} langCode - Language code
 * @returns {string} Native language name
 */
export function getLanguageName(langCode) {
  const names = {
    'vi': 'Tiếng Việt',
    'en': 'English'
  }
  
  return names[langCode] || 'English'
}

